import { useCallback, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Button } from 'framework/ui/mui-builder';
import { Modal } from 'framework/ui/ui-builder';
import { FormFieldsData, SubmissionAbortedError } from 'framework/form';
import usePositionSlotValidationService, {
  EmployeePositionSlotValidationResultDto,
} from 'domain/people/positionSlots/usePositionSlotValidationService';

type PendingSubmission = {
  submit: () => Promise<object>;
  resolve: (value: object) => void;
  reject: (error: Error) => void;
};

type UseContractPositionSlotValidationProps = {
  employeeAlias: string;
  positionSlotId: string | null;
};

const getStringValue = (value: unknown): string | null => (value ? String(value) : null);

const useContractPositionSlotValidation = ({
  employeeAlias,
  positionSlotId,
}: UseContractPositionSlotValidationProps) => {
  const { validateEmployeePositionSlotChange } = usePositionSlotValidationService();
  const [warningResult, setWarningResult] = useState<EmployeePositionSlotValidationResultDto | null>(null);
  const pendingSubmissionRef = useRef<PendingSubmission | null>(null);

  const validateAndSubmit = useCallback(
    async (values: FormFieldsData, submit: () => Promise<object>): Promise<object> => {
      const officeId = getStringValue(values.officeId);
      const contractType = getStringValue(values.contractType);

      if (!positionSlotId || !officeId || !contractType) {
        return submit();
      }

      let validationResult: EmployeePositionSlotValidationResultDto | null = null;
      try {
        validationResult = await validateEmployeePositionSlotChange({
          employeeAlias,
          positionSlotId,
          contractData: {
            officeId,
            contractType,
          },
        });
      } catch (error) {
        return submit();
      }

      if (validationResult?.errors?.length) {
        setWarningResult(validationResult);

        return new Promise<object>((resolve, reject) => {
          pendingSubmissionRef.current = {
            submit,
            resolve,
            reject,
          };
        });
      }

      return submit();
    },
    [employeeAlias, positionSlotId, validateEmployeePositionSlotChange],
  );

  const handleWarningCancel = useCallback(() => {
    const pendingSubmission = pendingSubmissionRef.current;
    pendingSubmissionRef.current = null;
    setWarningResult(null);

    if (pendingSubmission) {
      pendingSubmission.reject(new SubmissionAbortedError());
    }
  }, []);

  const handleWarningSubmit = useCallback(async () => {
    const pendingSubmission = pendingSubmissionRef.current;
    pendingSubmissionRef.current = null;
    setWarningResult(null);

    if (!pendingSubmission) {
      return;
    }

    try {
      pendingSubmission.resolve(await pendingSubmission.submit());
    } catch (error) {
      pendingSubmission.reject(error as Error);
    }
  }, []);

  const warningSubmitButton = (
    <Button key="position-slot-warning-submit" type="button" onClick={handleWarningSubmit}>
      Save anyway
    </Button>
  );

  const warningCancelButton = (
    <Button key="position-slot-warning-cancel" type="button" variant="outlined" onClick={handleWarningCancel}>
      Back to editing
    </Button>
  );

  const validationWarningModal = (
    <Modal
      isOpen={Boolean(warningResult)}
      title="Position slot may no longer fit"
      onToggle={handleWarningCancel}
      buttons={[warningSubmitButton, warningCancelButton]}
      hasCancelButton={false}
      paperStyles={{ background: 'var(--mui-palette-background-paper)' }}
    >
      <Box>
        <Typography>
          The current position slot may not match the updated contract data. Review the validation details below. You
          can save the changes anyway or return to editing.
        </Typography>
        <Box component="ul" sx={{ m: 0, mt: 1, pl: 3 }}>
          {warningResult?.errors.map(error => (
            <Box key={error} component="li">
              <Typography component="span" variant="body2">
                {error}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Modal>
  );

  return {
    validateAndSubmit,
    validationWarningModal,
  };
};

export default useContractPositionSlotValidation;
